import { AfterViewInit, ChangeDetectorRef, Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { AsyncPipe, Location, LowerCasePipe } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { TranslateModule } from '@ngx-translate/core';

import { Globals } from '@core/globals';
import { ButtonComponent } from '@atoms/button/button.component';
import { FormComponent } from '@atoms/form/form.component';
import { GridComponent } from '@atoms/grid/grid.component';
import { InputTitleComponent } from '@atoms/input-title/input-title.component';
import { LinkComponent } from '@atoms/link/link.component';
import { RangeSliderComponent } from '@atoms/range-slider/range-slider.component';
import { FloatNumberPipe } from '@pipes/float-number.pipe';
import { MonthPipe } from '@pipes/month.pipe';
import { PricePipe } from '@pipes/price.pipe';
import { GlobalsService } from '@services/globals.service';
import { GtmService } from '@services/gtm.service';
import { ModalService } from '@services/modal.service';
import { CarPaymentOptions, CarPaymentOptionsPlans } from '@models/car.model';
import { FinancingCalculator } from '@models/financing-calculator.model';
import { FinancingFormService } from '@services/financing-form.service';

@Component({
  selector: 'app-financing-calculator-form',
  imports: [AsyncPipe, LowerCasePipe, ReactiveFormsModule, TranslateModule, ButtonComponent, FormComponent, GridComponent, InputTitleComponent, LinkComponent, RangeSliderComponent, FloatNumberPipe, MonthPipe, PricePipe],
  templateUrl: './financing-calculator-form.component.html',
  styleUrl: './financing-calculator-form.component.scss',
})
export class FinancingCalculatorFormComponent implements OnInit, AfterViewInit {
  private cd = inject(ChangeDetectorRef);
  private location = inject(Location);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);
  private globalsService = inject(GlobalsService);
  public globals = inject(Globals);
  public gtmService = inject(GtmService);
  private modalService = inject(ModalService);
  private financingFormService = inject(FinancingFormService);
  @Input() type: 'default' | 'financing-form' | 'financing-modal' | 'online-modal' = 'default';
  @Input('car-id') carId?: number;
  @Input('car-price') carPrice?: number;
  @Input('car-payment-options') carPaymentOptions?: CarPaymentOptions[];
  @Input('active-advance') activeAdvance?: number;
  @Input('active-months') activeMonths?: number;
  @Input('active-loan-type') activeLoanType?: string;
  @Output('data-changed') dataChanged: EventEmitter<FinancingCalculator> = new EventEmitter();
  public defaultPercentage: number = 50;
  public percentage: number[] = [0, this.defaultPercentage];
  public months: number[] = [0, 60];
  public advance: number = 60000;
  public form = this.fb.nonNullable.group({
    loanType: ['1'],
  });
  public calculatedData?: CarPaymentOptionsPlans;
  public advanceSteps: number[] = [];
  public monthsSteps: number[] = [];

  percentageChanged(event: number[]): void {
    if (this.carPrice) this.advance = (this.carPrice / 100) * event[1];
    this.rangeSliderChanged();
  }

  percentageGtm(): void {
    this.gtm('akontace_soup', { akontace: this.percentage[1] });
  }

  repaymentPeriodChanged(): void {
    this.rangeSliderChanged();
  }

  repaymentPeriodGtm(): void {
    this.gtm('doba_splaceni_soup', { doba_splaceni: this.months[1] });
  }

  rangeSliderChanged(): void {
    this.dataChange(true);
    this.calculate();
  }

  openCallUsModal(): void {
    this.modalService.open('<app-call-us-modal></app-call-us-modal>', 'medium');
  }

  dataChange(reset: boolean): void {
    const data = {
      advance: this.advance,
      loanType: this.form.controls.loanType.value,
      months: this.months[1],
      calculatedData: this.calculatedData,
    };
    if (reset) this.financingFormService.product.set(undefined);
    this.dataChanged.emit(data);
  }

  gtm(id: string, data?: any): void {
    const event = { event: 'click_calc_' + id };
    const dataLayer = { ...event, ...data };
    this.gtmService.pushGtmPageType(dataLayer);
  }

  onlineFormSubmit(): void {
    this.modalService.close();
  }

  calculate(): void {
    const percentage = this.percentage[1];
    const months = this.months[1];

    if (this.carPaymentOptions) {
      const selectedOption = this.carPaymentOptions.find((item) => item.advance === percentage);
      if (selectedOption) {
        const selectedPlan = selectedOption.paymentPlans.find((plan) => plan.numberOfMonths === months);
        if (selectedPlan) this.calculatedData = selectedPlan;
      }
    }
  }

  ngOnInit(): void {
    if (!this.carPrice) return;

    const hash = this.location.path(true).split('#')[1];
    const fragmentParams = this.globalsService.getFragmentParams(hash);
    const months = fragmentParams['months'] !== undefined && fragmentParams['months'] !== null ? Number(fragmentParams['months']) : null;
    const advancePrice = fragmentParams['advance'] !== undefined && fragmentParams['advance'] !== null ? Number(fragmentParams['advance']) : null;

    if (this.carPaymentOptions?.length) {
      this.advanceSteps = this.carPaymentOptions.map((item) => item.advance) ?? [];
      this.advanceSteps.sort((a, b) => a - b);
      this.monthsSteps = this.carPaymentOptions[0]?.paymentPlans.map((item) => item.numberOfMonths) ?? [];
      this.monthsSteps.sort((a, b) => a - b);
    }

    if (this.activeMonths) this.months = [0, this.activeMonths];
    if (this.activeLoanType) this.form.controls.loanType.setValue(this.activeLoanType);
    if (advancePrice !== null && months !== null) {
      let foundMonths = false;
      let foundAdvance = false;
      this.carPaymentOptions?.forEach((item) => {
        item.paymentPlans.forEach((plan) => {
          if (plan.numberOfMonths === months) {
            this.months = [0, months];
            foundMonths = true;
            if (plan.advancePrice === advancePrice) {
              foundAdvance = true;
              this.advance = advancePrice;
              this.percentage = [0, item.advance];
            }
          }
        });
        if (foundAdvance) return;
      });
      if (!foundMonths) this.months = [0, 60];
      if (!foundAdvance) {
        this.advance = (this.carPrice / 100) * this.defaultPercentage;
        this.percentage = [0, this.defaultPercentage];
      }
    } else {
      if (this.activeAdvance) {
        this.advance = this.activeAdvance;
        const percentage = (this.activeAdvance / this.carPrice) * 100;
        this.percentage = [0, percentage];
      } else {
        this.advance = (this.carPrice / 100) * this.defaultPercentage;
      }
    }
    this.calculate();
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.gtm('kategorie_zakazniku', { kategorie_zakazniku: this.form.value.loanType });
      this.dataChange(false);
    });
  }

  ngAfterViewInit(): void {
    this.dataChange(false);
    this.cd.detectChanges();
  }
}
